import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Users,
  Settings,
  Trash2,
  UserCheck,
  UserX,
  RefreshCw,
  Clock,
  Image,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { CacheManager } from "@/components/CacheManager";

interface User {
  id: number;
  username: string;
  admin: boolean;
  created_at: string;
}

interface AppSettings {
  allow_new_users: boolean;
  max_file_size: number;
  allowed_file_types: string[];
}

export function AdminPage() {
  const { user, token } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"users" | "settings" | "cache">(
    "users"
  );
  const [isResettingWatchTimes, setIsResettingWatchTimes] = useState(false);
  const [isGeneratingThumbnails, setIsGeneratingThumbnails] = useState(false);
  const [thumbnailResult, setThumbnailResult] = useState<{
    processed: number;
    succeeded: number;
    failed: number;
  } | null>(null);
  const [isProcessingFaststart, setIsProcessingFaststart] = useState(false);
  const [faststartResult, setFaststartResult] = useState<{
    processed: number;
    succeeded: number;
    failed: number;
    skipped: number;
  } | null>(null);

  useEffect(() => {
    if (user?.admin) {
      fetchUsers();
      fetchSettings();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await fetch("/api/admin/users", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch users");
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch("/api/admin/settings", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch settings");
      }

      const data = await response.json();
      setSettings(data.settings || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId: number) => {
    if (!confirm("Are you sure you want to delete this user?")) return;

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete user");
      }

      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  };

  const updateSettings = async (newSettings: Partial<AppSettings>) => {
    try {
      const response = await fetch("/api/admin/settings", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newSettings),
      });

      if (!response.ok) {
        throw new Error("Failed to update settings");
      }

      fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    }
  };

  const resetAllWatchTimes = async () => {
    if (
      !confirm(
        "Are you sure you want to reset all watch times to 0? This action cannot be undone."
      )
    ) {
      return;
    }

    setIsResettingWatchTimes(true);
    try {
      const response = await fetch("/api/admin/reset-watch-times", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to reset watch times");
      }

      const data = await response.json();
      alert(
        `Successfully reset watch times for ${data.resetCount} media items.`
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to reset watch times"
      );
    } finally {
      setIsResettingWatchTimes(false);
    }
  };

  const generateMissingThumbnails = async () => {
    if (
      !confirm(
        "This will generate thumbnails for all media items that are missing them. This may take a while. Continue?"
      )
    ) {
      return;
    }

    setIsGeneratingThumbnails(true);
    setThumbnailResult(null);
    try {
      const response = await fetch("/api/admin/generate-thumbnails", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to generate thumbnails");
      }

      const data = await response.json();
      setThumbnailResult({
        processed: data.processed,
        succeeded: data.succeeded,
        failed: data.failed,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to generate thumbnails"
      );
    } finally {
      setIsGeneratingThumbnails(false);
    }
  };

  const processFaststart = async () => {
    if (
      !confirm(
        "This will re-encode all existing MP4/MOV videos with faststart so playback begins immediately. This may take a very long time for large libraries. Continue?"
      )
    ) {
      return;
    }

    setIsProcessingFaststart(true);
    setFaststartResult(null);
    try {
      const response = await fetch("/api/admin/process-faststart", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to process faststart");
      }

      const data = await response.json();
      setFaststartResult({
        processed: data.processed,
        succeeded: data.succeeded,
        failed: data.failed,
        skipped: data.skipped,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to process faststart"
      );
    } finally {
      setIsProcessingFaststart(false);
    }
  };

  if (!user?.admin) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-destructive">
              Access denied. Admin privileges required.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Admin Panel</h1>
        <p className="text-muted-foreground">
          Manage users, application settings, and PWA cache
        </p>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button onClick={() => setError(null)} className="mt-2">
              Dismiss
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 border-b">
        <button
          onClick={() => setActiveTab("users")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "users"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Users className="h-4 w-4 inline mr-2" />
          Users
        </button>
        <button
          onClick={() => setActiveTab("settings")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "settings"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Settings className="h-4 w-4 inline mr-2" />
          Settings
        </button>
        <button
          onClick={() => setActiveTab("cache")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "cache"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <RefreshCw className="h-4 w-4 inline mr-2" />
          Cache
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === "users" && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No users found
                  </p>
                ) : (
                  users.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between p-4 border rounded"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {u.admin ? (
                            <UserCheck className="h-5 w-5 text-green-500" />
                          ) : (
                            <UserX className="h-5 w-5 text-muted-foreground" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p
                              className="font-medium truncate max-w-[200px] cursor-help"
                              title={u.username}
                            >
                              {u.username}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {u.admin ? "Administrator" : "User"} • Joined{" "}
                              {new Date(u.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      {u.id !== user.id && (
                        <Button
                          onClick={() => deleteUser(u.id)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Application Settings</CardTitle>
            </CardHeader>
            <CardContent>
              {settings ? (
                <div className="space-y-6">
                  {/* User Registration */}
                  <div className="space-y-2">
                    <Label>User Registration</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="allow-new-users"
                        checked={settings.allow_new_users}
                        onChange={(e) =>
                          updateSettings({ allow_new_users: e.target.checked })
                        }
                        className="rounded"
                      />
                      <label htmlFor="allow-new-users" className="text-sm">
                        Allow new user registrations
                      </label>
                    </div>
                  </div>

                  {/* File Upload Limits */}
                  <div className="space-y-2">
                    <Label htmlFor="max-file-size">
                      Maximum File Size (MB)
                    </Label>
                    <Input
                      id="max-file-size"
                      type="number"
                      value={Math.round(settings.max_file_size)}
                      onChange={(e) =>
                        updateSettings({
                          max_file_size: parseInt(e.target.value) * 1024 * 1024,
                        })
                      }
                      className="w-32"
                    />
                  </div>

                  {/* Allowed File Types */}
                  <div className="space-y-2">
                    <Label htmlFor="allowed-types">
                      Allowed File Types (comma-separated)
                    </Label>
                    <Input
                      id="allowed-types"
                      value={settings.allowed_file_types.join(", ")}
                      onChange={(e) =>
                        updateSettings({
                          allowed_file_types: e.target.value
                            .split(",")
                            .map((type) => type.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder="image/jpeg, image/png, video/mp4"
                    />
                  </div>

                  <div className="pt-4">
                    <p className="text-xs text-muted-foreground">
                      Changes are automatically saved when you modify settings.
                    </p>
                  </div>

                  {/* Reset Watch Times */}
                  <div className="pt-6 border-t">
                    <Label>Data Management</Label>
                    <div className="mt-2">
                      <Button
                        onClick={resetAllWatchTimes}
                        disabled={isResettingWatchTimes}
                        variant="destructive"
                        size="sm"
                      >
                        <Clock className="h-4 w-4 mr-2" />
                        {isResettingWatchTimes
                          ? "Resetting..."
                          : "Reset All Watch Times"}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">
                        This will reset all media watch times and view counts to
                        0. This action cannot be undone.
                      </p>
                    </div>

                    <div className="mt-4">
                      <Button
                        onClick={generateMissingThumbnails}
                        disabled={isGeneratingThumbnails}
                        variant="outline"
                        size="sm"
                      >
                        <Image className="h-4 w-4 mr-2" />
                        {isGeneratingThumbnails
                          ? "Generating..."
                          : "Generate Missing Thumbnails"}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">
                        Generate thumbnails for all media items that are missing
                        them. This may take a while for large libraries.
                      </p>
                      {thumbnailResult && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Done: {thumbnailResult.succeeded} succeeded,{" "}
                          {thumbnailResult.failed} failed out of{" "}
                          {thumbnailResult.processed} processed.
                        </p>
                      )}
                    </div>

                    <div className="mt-4">
                      <Button
                        onClick={processFaststart}
                        disabled={isProcessingFaststart}
                        variant="outline"
                        size="sm"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {isProcessingFaststart
                          ? "Processing..."
                          : "Apply Faststart to Existing Videos"}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">
                        Re-encodes all existing MP4/MOV videos so playback
                        begins immediately without buffering. Run once for your
                        existing library — new uploads are processed
                        automatically.
                      </p>
                      {faststartResult && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Done: {faststartResult.succeeded} succeeded,{" "}
                          {faststartResult.failed} failed,{" "}
                          {faststartResult.skipped} skipped out of{" "}
                          {faststartResult.processed + faststartResult.skipped}{" "}
                          total.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Loading settings...</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cache Management Tab */}
      {activeTab === "cache" && (
        <div className="space-y-4">
          <CacheManager />
        </div>
      )}
    </div>
  );
}
